import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from "@material-ui/core/styles";
import { useHistory } from 'react-router-dom';
import AdminLayout from '../../layouts/AdminLayout';
import Pagination from '../../components/Pagination';
import ConfirmDialog from '../../components/ConfirmDialog';
import { getDuas, deleteDua, reorderDua, getDuaCategories } from '../../services/duaService';
import {
  Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Button, IconButton, Chip, TextField, MenuItem, Box, makeStyles, CircularProgress, Snackbar, Tooltip, Typography
} from '@material-ui/core';
import { getAllDuaSubcategories } from '../../services/duaSubcategoryService';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import AddIcon from '@material-ui/icons/Add';
import DuaFormModal from './DuaFormModal';

const useStyles = makeStyles((theme) => ({
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(3),
    flexWrap: 'wrap',
    gap: theme.spacing(2),
  },
  filterRow: {
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  filterField: {
    minWidth: 180,
    '& .MuiInputBase-root': {
      
    },
  },
  tableContainer: {
    borderRadius: 16,
    boxShadow: '0 4px 24px rgba(0,0,0,0.2)',
    overflow: 'hidden',
    border: `1px solid ${theme.palette.divider}`,
  },
  statusPublished: {
    backgroundColor: 'rgba(16,185,129,0.15)',
    color: '#10b981',
    border: '1px solid rgba(16,185,129,0.3)',
    fontWeight: 600,
  },
  statusDraft: {
    backgroundColor: 'rgba(245,158,11,0.15)',
    color: '#f59e0b',
    border: '1px solid rgba(245,158,11,0.3)',
    fontWeight: 600,
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 400,
    background: theme.palette.background.paper,
    borderRadius: 16,
    border: `1px solid ${theme.palette.divider}`,
  },
  loadingText: {
    marginTop: 16,
    color: theme.palette.text.secondary,
    fontWeight: 500,
  },
  actionBtn: {
    color: theme.palette.text.secondary,
    padding: 8,
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      color: theme.palette.text.primary,
    },
  },
  deleteBtn: {
    color: theme.palette.text.secondary,
    padding: 8,
    '&:hover': {
      backgroundColor: 'rgba(239,68,68,0.1)',
      color: '#ef4444',
    },
  },
  orderBadge: {
    background: theme.palette.action.hover,
    color: theme.palette.text.secondary,
    padding: '4px 8px',
    borderRadius: 6,
    fontWeight: 600,
    fontSize: '0.8rem',
  },
  categoryBadge: {
    background: 'rgba(56,189,248,0.1)',
    color: '#38bdf8',
    border: '1px solid rgba(56,189,248,0.2)',
    fontWeight: 600,
  },
  countChip: {
    background: 'linear-gradient(135deg, rgba(16,185,129,0.15) 0%, rgba(5,150,105,0.15) 100%)',
    color: '#10b981',
    border: '1px solid rgba(16,185,129,0.3)',
    fontWeight: 700,
    fontSize: '0.85rem',
    height: 36,
    padding: '0 8px',
    borderRadius: 10,
  },
}));

export default function DuaListPage() {
  const classes = useStyles();
  
  const theme = useTheme();
  const history = useHistory();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });
  const [categories, setCategories] = useState([]);
  const [filterSubcategory, setFilterSubcategory] = useState('all');
  const [subcategories, setSubcategories] = useState([]);
  const [allSubcategories, setAllSubcategories] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [selectedDuaId, setSelectedDuaId] = useState(null);

  const handleOpenCreateModal = () => {
    setSelectedDuaId(null);
    setFormModalOpen(true);
  };

  const handleOpenEditModal = (duaId) => {
    setSelectedDuaId(duaId);
    setFormModalOpen(true);
  };

  const handleCloseFormModal = () => {
    setFormModalOpen(false);
    setSelectedDuaId(null);
  };

  const handleFormSuccess = () => {
    setSnackbar({
      open: true,
      message: selectedDuaId ? 'দো‘আ সফলভাবে আপডেট করা হয়েছে!' : 'নতুন দো‘আ সফলভাবে তৈরি করা হয়েছে!'
    });
    loadData({ page });
  };

  useEffect(() => {
    let mounted = true;
    // Load categories fresh from Firestore
    getDuaCategories().then((cats) => {
      if (mounted) setCategories(cats);
    });
    getAllDuaSubcategories().then((subs) => {
      if (mounted) setAllSubcategories(subs.filter(s => !s.isDeleted));
    });
    return () => { mounted = false; };
  }, []);

  const getCategoryName = useCallback((sourceId) => {
    const cat = categories.find((b) => b.id === sourceId);
    return cat ? cat.nameBn || cat.nameEn || cat.name : (sourceId || 'Unknown');
  }, [categories]);

  const getSubcategoryName = useCallback((subId) => {
    const sub = allSubcategories.find((s) => s.id === subId);
    return sub ? sub.titleBn || sub.titleEn : null;
  }, [allSubcategories]);

  const loadData = useCallback(async (opts = {}) => {
    setLoading(true);
    const targetPage = opts.page !== undefined ? opts.page : 1;
    try {
      const result = await getDuas({
        page: targetPage,
        category: filterCategory,
        subCategoryId: filterSubcategory,
        status: filterStatus,
        search: searchText,
      });
      setItems(result.items);
      setTotalCount(result.total);
      setTotalPages(result.totalPages);
      setPage(result.page);
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to load duas' });
    }
    setLoading(false);
  }, [filterCategory, filterSubcategory, filterStatus, searchText]);

  useEffect(() => {
    // Add debounce for search text
    const delayDebounceFn = setTimeout(() => {
      loadData({ page: 1 });
    }, 300);
    return () => clearTimeout(delayDebounceFn);
  }, [searchText, filterCategory, filterSubcategory, filterStatus]);

  useEffect(() => {
    let mounted = true;
    if (mounted) {
      if (filterCategory !== 'all') {
        const subs = allSubcategories.filter(s => s.categoryId === filterCategory);
        setSubcategories(subs);
      } else {
        setSubcategories(allSubcategories);
      }
    }
    setFilterSubcategory('all');
    return () => { mounted = false; };
  }, [filterCategory, allSubcategories]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
    loadData({ page: newPage });
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteDua(deleteTarget.id);
      setSnackbar({ open: true, message: 'Dua removed successfully' });
      setDeleteTarget(null);
      loadData({ page });
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to delete dua' });
    }
  };

  const handleReorder = async (item, direction) => {
    const idx = items.indexOf(item);
    const targetIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (targetIdx < 0 || targetIdx >= items.length) return;
    const target = items[targetIdx];
    try {
      await Promise.all([
        reorderDua(item.id, target.orderIndex),
        reorderDua(target.id, item.orderIndex),
      ]);
      loadData();
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to reorder' });
    }
  };

  return (
    <AdminLayout title="Dua Repository">
      <div className={classes.toolbar}>
        <div className={classes.filterRow}>
          <TextField
            placeholder="Search Dua (English/Bangla)"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className={classes.filterField}
            variant="outlined"
            size="small"
          />
          <TextField
            select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className={classes.filterField}
            variant="outlined"
            size="small"
            InputProps={{
              startAdornment: <Box component="span" pr={1} color="#64748b" fontSize="0.85rem" fontWeight={600}>CAT:</Box>
            }}
          >
            <MenuItem value="all">All Categories</MenuItem>
            {categories.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.nameBn || c.nameEn || c.name}
              </MenuItem>
            ))}
          </TextField>
          {filterCategory !== 'all' && (
            <TextField
              select
              value={filterSubcategory}
              onChange={(e) => setFilterSubcategory(e.target.value)}
              className={classes.filterField}
              variant="outlined"
              size="small"
              InputProps={{
                startAdornment: <Box component="span" pr={1} color="#64748b" fontSize="0.85rem" fontWeight={600}>SUB:</Box>
              }}
            >
              <MenuItem value="all">All Subcategories</MenuItem>
              {subcategories.map((sub) => (
                <MenuItem key={sub.id} value={sub.id}>
                  {sub.titleBn || sub.titleEn}
                </MenuItem>
              ))}
            </TextField>
          )}
          <TextField
            select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className={classes.filterField}
            variant="outlined"
            size="small"
            InputProps={{
              startAdornment: <Box component="span" pr={1} color="#64748b" fontSize="0.85rem" fontWeight={600}>STATUS:</Box>
            }}
          >
            <MenuItem value="all">All Records</MenuItem>
            <MenuItem value="published">Published Only</MenuItem>
            <MenuItem value="draft">Drafts Only</MenuItem>
          </TextField>
        </div>
        <Box display="flex" alignItems="center" flexWrap="wrap" style={{ gap: 16 }}>
          <Chip
            label={`Total Duas: ${totalCount}`}
            className={classes.countChip}
          />
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleOpenCreateModal}
            disableElevation
          >
            Add New Dua
          </Button>
        </Box>
      </div>

      {loading ? (
        <div className={classes.loadingContainer}>
          <CircularProgress size={40} thickness={4} />
          <Typography className={classes.loadingText}>Loading records...</Typography>
        </div>
      ) : (
        <TableContainer component={Paper} className={classes.tableContainer} elevation={0}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width="80">Order</TableCell>
                <TableCell width="30%">Title (English)</TableCell>
                <TableCell width="25%">Title (Bangla)</TableCell>
                <TableCell width="120">Category</TableCell>
                <TableCell width="120">Subcategory</TableCell>
                <TableCell width="100">Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item, idx) => (
                <TableRow key={item.id}>
                  <TableCell>
                    <span className={classes.orderBadge}>{item.orderIndex}</span>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="textPrimary" style={{ fontWeight: 500 }}>
                      {item.title?.en?.substring(0, 50)}
                      {item.title?.en?.length > 50 ? '...' : ''}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" style={{ color: theme.palette.text.secondary }}>
                      {item.title?.bn?.substring(0, 50)}
                      {item.title?.bn?.length > 50 ? '...' : ''}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={getCategoryName(item.category)} className={classes.categoryBadge} />
                  </TableCell>
                  <TableCell>
                    {getSubcategoryName(item.subCategoryId) ? (
                      <Chip size="small" label={getSubcategoryName(item.subCategoryId)} style={{ background: 'rgba(99,102,241,0.1)', color: '#6366f1', border: '1px solid rgba(99,102,241,0.2)', fontWeight: 600 }} />
                    ) : (
                      <Typography variant="caption" color="textSecondary">—</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={item.isPublished ? 'Published' : 'Draft'}
                      className={item.isPublished ? classes.statusPublished : classes.statusDraft}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Box display="flex" justifyContent="flex-end" style={{ gap: 6 }}>
                      <Tooltip title="Move Up">
                        <IconButton className={classes.actionBtn} onClick={() => handleReorder(item, 'up')}>
                          <ArrowUpwardIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Move Down">
                        <IconButton className={classes.actionBtn} onClick={() => handleReorder(item, 'down')}>
                          <ArrowDownwardIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton className={classes.actionBtn} onClick={() => handleOpenEditModal(item.id)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton className={classes.deleteBtn} onClick={() => setDeleteTarget(item)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              {items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" style={{ padding: "48px 0" }} color="textSecondary">
                    No records found for the selected criteria.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!loading && totalCount > 0 && (
        <Pagination
          page={page}
          totalPages={totalPages}
          totalCount={totalCount}
          pageSize={20}
          onPageChange={handlePageChange}
        />
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete Record"
        message="Are you sure you want to permanently remove this Dua from the database? This action cannot be undone."
        confirmText="Delete"
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      <DuaFormModal
        open={formModalOpen}
        duaId={selectedDuaId}
        onClose={handleCloseFormModal}
        onSuccess={handleFormSuccess}
      />

      <Snackbar
        open={snackbar.open}
        message={snackbar.message}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </AdminLayout>
  );
}